import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { Search, Loader2 } from 'lucide-react';

// Component to handle map clicks
const MapEvents = ({ onLocationSelect, setMarkerPosition }) => {
  useMapEvents({
    click(e) {
      const newPosition = { lat: e.latlng.lat, lng: e.latlng.lng };
      setMarkerPosition(newPosition);
      if (onLocationSelect) {
        onLocationSelect(newPosition);
      }
    },
  });
  return null;
};

// Component to re-center map when markerPosition changes externally
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center.lat && center.lng) {
      map.setView([center.lat, center.lng]);
    }
  }, [center, map]);
  return null;
};

const GoogleMapComponent = ({ initialCenter, onLocationSelect, height = "500px", showSearch = false }) => {
  const [markerPosition, setMarkerPosition] = useState(initialCenter || { lat: 30.3165, lng: 78.0322 });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const markerRef = useRef(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const newPosition = {
            lat: marker.getLatLng().lat,
            lng: marker.getLatLng().lng,
          };
          setMarkerPosition(newPosition);
          if (onLocationSelect) {
            onLocationSelect(newPosition);
          }
        }
      },
    }),
    [onLocationSelect]
  );

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`);
      const data = await res.json();
      setSearchResults(data);
    } catch (error) {
      console.error("Search error", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectResult = (result) => {
    const newPosition = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon)
    };
    setMarkerPosition(newPosition);
    setSearchResults([]);
    setSearchQuery(''); // clear after selection
    if (onLocationSelect) {
      onLocationSelect(newPosition, result.display_name);
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      {showSearch && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: '90%',
          maxWidth: '450px'
        }}>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch(e);
                }
              }}
              placeholder="Search any location..."
              className="w-full pl-10 pr-16 py-2.5 bg-background/95 backdrop-blur-md border border-border rounded-xl text-foreground placeholder:text-muted-foreground shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </div>
            <button 
              type="button" 
              onClick={handleSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary hover:text-primary/80 text-sm font-medium"
            >
              Find
            </button>
          </div>

          {/* Search Results Dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background border border-border rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto z-[1001]">
              {searchResults.map((result) => (
                <button
                  key={result.place_id}
                  type="button"
                  onClick={() => handleSelectResult(result)}
                  className="w-full text-left px-4 py-3 text-sm text-foreground hover:bg-muted transition-colors border-b border-border/50 last:border-0"
                >
                  {result.display_name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <MapContainer
        center={[markerPosition.lat, markerPosition.lng]}
        zoom={13}
        style={{ width: '100%', height: '100%', borderRadius: '12px', border: '1px solid rgba(110, 231, 183, 0.3)' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater center={markerPosition} />
        <MapEvents onLocationSelect={onLocationSelect} setMarkerPosition={setMarkerPosition} />
        <Marker
          position={[markerPosition.lat, markerPosition.lng]}
          draggable={true}
          eventHandlers={eventHandlers}
          ref={markerRef}
        />
      </MapContainer>
    </div>
  );
};

export default GoogleMapComponent;
